// /team API layer (S9) — 維運團隊帳號與角色治理。自 TeamPage 抽出以利單元測試;
// 契約不變:grant/revoke 為 platform-owner only + reason/case_id + 伺服端稽核;
// create member = 預建維運 domain 帳號;reset = 寄一次性重設連結(平台不接觸密碼值)。
import { apiFetch } from '../../lib/api';
import type { AdminUser } from '../users/usersApi';

export interface GrantRoleResult {
  user_id: string;
  role: string;
  previous_role: string;
  updated_at: number;
}

/** POST /v3/admin/users/{id}/grant-role — 授予角色(platform-owner only)。 */
export async function grantRole(userId: string, role: string, reason: string, caseId: string): Promise<GrantRoleResult> {
  const res = await apiFetch<GrantRoleResult>(`/v3/admin/users/${encodeURIComponent(userId)}/grant-role`, {
    method: 'POST',
    body: { role, reason, case_id: caseId },
  });
  return res.data;
}

export interface CreateMemberResult {
  member: AdminUser;
  invited: boolean;
}

/** POST /v3/admin/team/members — 預先建立維運 domain 帳號(platform-owner only)。
 *  email 必須在平台 domain;initial_password 空 → 後端寄設定密碼邀請信。 */
export async function createTeamMember(input: {
  email: string;
  display_name: string;
  role: string;
  initial_password?: string;
  reason: string;
  case_id: string;
}): Promise<CreateMemberResult> {
  const res = await apiFetch<CreateMemberResult>('/v3/admin/team/members', { method: 'POST', body: input });
  return res.data;
}

/** POST /v3/admin/users/{id}/revoke-role — maintainer role 歸還 `user`(platform-owner only;
 *  後端另擋自撤、platform-owner 目標與非 maintainer 目標,422)。 */
export async function revokeRole(userId: string, reason: string, caseId: string): Promise<GrantRoleResult> {
  const res = await apiFetch<GrantRoleResult>(`/v3/admin/users/${encodeURIComponent(userId)}/revoke-role`, {
    method: 'POST',
    body: { reason, case_id: caseId },
  });
  return res.data;
}

export interface PasswordResetResult {
  user_id: string;
  channel: string;
  sent: boolean;
  expires_in: number;
}

/** POST /v3/admin/users/{id}/reset-password — operator 觸發密碼重設:寄一次性連結(15 分鐘)指向
 *  本 console `/reset-password?token=`,收件人自行設定新密碼;平台/操作者全程不接觸密碼值。
 *  reason/case_id 選填(僅入稽核)。 */
export async function sendPasswordReset(userId: string, reason: string, caseId: string): Promise<PasswordResetResult> {
  const res = await apiFetch<PasswordResetResult>(`/v3/admin/users/${encodeURIComponent(userId)}/reset-password`, {
    method: 'POST',
    body: { channel: 'email', reason, case_id: caseId },
  });
  return res.data;
}

export interface OperatorProfileResult {
  user_id: string;
  display_name: string;
  updated_at: number;
}

/** PATCH /v3/admin/users/{id} — 編輯維運帳號 display_name(platform-owner only;1-32 字)。
 *  只改 display_name;角色走 grant/revoke、email/phone 走各自流程。 */
export async function editOperatorProfile(userId: string, displayName: string, reason: string, caseId: string): Promise<OperatorProfileResult> {
  const res = await apiFetch<OperatorProfileResult>(`/v3/admin/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: { display_name: displayName, reason, case_id: caseId },
  });
  return res.data;
}

export interface OperatorStatusResult {
  status: string;
}

/** POST /v3/admin/users/{id}/deactivate — 停用維運帳號(operator off-board;platform-owner only)。
 *  operator 語意,有別於客訴 suspend/ban;後端擋自我停用(409 CANNOT_SELF_DEACTIVATE)與
 *  platform-owner 目標(409 CANNOT_DEACTIVATE_OWNER)。 */
export async function deactivateOperator(userId: string, reason: string, caseId: string): Promise<OperatorStatusResult> {
  const res = await apiFetch<OperatorStatusResult>(`/v3/admin/users/${encodeURIComponent(userId)}/deactivate`, {
    method: 'POST',
    body: { reason, case_id: caseId },
  });
  return res.data;
}

/** POST /v3/admin/users/{id}/reactivate — 復用維運帳號(deactivated → active;platform-owner only)。 */
export async function reactivateOperator(userId: string, reason: string, caseId: string): Promise<OperatorStatusResult> {
  const res = await apiFetch<OperatorStatusResult>(`/v3/admin/users/${encodeURIComponent(userId)}/reactivate`, {
    method: 'POST',
    body: { reason, case_id: caseId },
  });
  return res.data;
}
